import { inject, injectable } from 'inversify'
import { get, toLower } from 'lodash-es'
import type { PaginationUrlFilterScalarValueType } from '@/services/tables/types'
import {
  type DependencyToPreload,
  type FilterDependencyProvider,
  type FilterToFormattedTextConvertable,
  type FormattedTextNode,
  FilterOperationEnum,
  type FormattedTextBadgesValueItem,
} from '@/services/types'
import type TagService from '@/services/domain/tag/tagService'
import { SERVICE_TYPES } from '@/core/container/types'
import type { FilterToCreate } from '@/services/tables/filters/types'
import Tag from '@/data/models/domain/Tag'
import type TranslateService from '@/services/translateService'
import type DeletedEntityService from '@/services/deletedEntityService'
import type { TmBadgeDeletedEntityEntities } from '@/components/shared/badge/types'

const availableOperations = [
  FilterOperationEnum.in,
  FilterOperationEnum.allOf,
  FilterOperationEnum.exactly,
  FilterOperationEnum.notIn,
] as const

type AvailableOperation = (typeof availableOperations)[number]

const entity: TmBadgeDeletedEntityEntities = 'tag'

@injectable()
export default class TagFilterMapper implements FilterToFormattedTextConvertable, FilterDependencyProvider {
  constructor(
    @inject(SERVICE_TYPES.TagService) protected readonly tagService: TagService,
    @inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService,
    @inject(SERVICE_TYPES.DeletedEntityService) protected readonly deletedEntityService: DeletedEntityService,
  ) {}

  public toFormattedText(
    filterValue: PaginationUrlFilterScalarValueType,
    { relatedFieldName = 'id' }: FilterToCreate,
  ): FormattedTextNode[] {
    const value = this.getParsedValue(filterValue, relatedFieldName)

    return [
      {
        type: 'italic',
        value: this.getOperationLabel(value.operation),
      },
      {
        type: 'badges',
        value: value.tagIds.map((id) => {
          if (this.deletedEntityService.isDeleted(entity, id)) {
            return {
              name: toLower(this.deletedEntityService.getDeletedEntityLabel(entity)),
              color: 'red',
            } satisfies FormattedTextBadgesValueItem
          }
          return this.tagService.findEntityByIdOrNull(id)?.title ?? id
        }),
      },
    ]
  }

  public getFilterDependencies(
    filterValue: PaginationUrlFilterScalarValueType,
    { relatedFieldName = 'id' }: FilterToCreate,
  ): DependencyToPreload[] {
    return [
      {
        model: Tag,
        ids: this.getParsedValue(filterValue, relatedFieldName).tagIds,
      },
    ]
  }

  protected getParsedValue(filterValue: PaginationUrlFilterScalarValueType, relatedFieldName: string) {
    let operation: AvailableOperation = FilterOperationEnum.in
    let tagIds: string[] = []

    for (const op of availableOperations) {
      const ids = get(filterValue, [relatedFieldName, op]) as string[] | undefined
      if (ids && ids.length > 0) {
        operation = op
        tagIds = ids
        break
      }
    }

    return {
      operation,
      tagIds,
    }
  }

  public getOperationLabel(operation: AvailableOperation) {
    const operationLabelMap: Record<AvailableOperation, string> = {
      [FilterOperationEnum.in]: this.translateService.t('tmFilterTags.contain'),
      [FilterOperationEnum.allOf]: this.translateService.t('tmFilterTags.containAll'),
      [FilterOperationEnum.exactly]: this.translateService.t('tmFilterTags.areExactly'),
      [FilterOperationEnum.notIn]: this.translateService.t('tmFilterTags.containNone'),
    }
    return operationLabelMap[operation]
  }
}
